import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Loader2, ArrowRight, Sparkles } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SearchBox = ({ className = '', onClose }) => {
  const { currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search using the autocomplete API
  const searchProducts = useCallback(async (searchQuery) => {
    if (searchQuery.length < 2) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/search/autocomplete?q=${encodeURIComponent(searchQuery)}&limit=8`);
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        setSuggestions(data.results);
        setIsOpen(true);
      } else {
        setSuggestions([]);
        setIsOpen(true); // Show "no results" message
      }
    } catch (error) {
      console.error('Search error:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce search input
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    if (query.length >= 2) {
      debounceRef.current = setTimeout(() => {
        searchProducts(query);
      }, 300);
    } else {
      setSuggestions([]);
      setIsOpen(false);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, searchProducts]);

  const getProductName = (product) => {
    if (!product?.name) return 'Product';
    return product.name[currentLanguage] || product.name.en || 'Product';
  };

  const formatPrice = useCurrency().formatPrice;

  const handleSelect = (product) => {
    setQuery('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    if (onClose) onClose();
    navigate(`/product/${product.id}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
      handleSelect(suggestions[highlightedIndex]);
    } else if (query.length >= 2) {
      setIsOpen(false);
      if (onClose) onClose();
      navigate(`/products?search=${encodeURIComponent(query)}`);
    }
  };

  const clearSearch = () => {
    setQuery('');
    setSuggestions([]);
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const translations = {
    en: {
      placeholder: 'Search peptides, products...',
      noResults: 'No products found',
      viewAll: 'View all results'
    },
    es: {
      placeholder: 'Buscar péptidos, productos...',
      noResults: 'No se encontraron productos',
      viewAll: 'Ver todos los resultados'
    },
    ru: {
      placeholder: 'Поиск пептидов, продуктов...',
      noResults: 'Продукты не найдены',
      viewAll: 'Все результаты'
    }
  };

  const t = translations[currentLanguage] || translations.en;

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-capsule-teal" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => query.length >= 2 && suggestions.length > 0 && setIsOpen(true)}
            placeholder={t.placeholder}
            className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-capsule-teal/50 focus:border-capsule-teal bg-gray-50 hover:bg-white transition-all"
            data-testid="search-input"
          />
          {loading && (
            <div className="absolute right-10 top-1/2 transform -translate-y-1/2">
              <Loader2 className="h-4 w-4 animate-spin text-capsule-teal" />
            </div>
          )}
          {query && !loading && (
            <button
              type="button"
              onClick={clearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              data-testid="clear-search-btn"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </form>

      {/* Suggestions Dropdown - Capsule 8 Design */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden" data-testid="search-suggestions">
          {suggestions.length > 0 ? (
            <>
              <ul className="max-h-80 overflow-y-auto">
                {suggestions.map((product, index) => (
                  <li key={product.id}>
                    <button
                      type="button"
                      onClick={() => handleSelect(product)}
                      className={`w-full px-4 py-3 flex items-center gap-3 transition-colors text-left ${
                        highlightedIndex === index 
                          ? 'bg-capsule-teal/10' 
                          : 'hover:bg-capsule-teal/5'
                      }`}
                      data-testid={`search-result-${product.id}`}
                    >
                      <div className="w-12 h-12 bg-gradient-to-br from-gray-50 to-capsule-bg rounded-xl flex-shrink-0 overflow-hidden border border-gray-100">
                        <img
                          src={product.image_url || '/placeholder-product.png'}
                          alt={getProductName(product)}
                          className="w-full h-full object-contain p-1"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/placeholder-product.png';
                          }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {getProductName(product)}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-sm font-bold text-capsule-teal">
                            {formatPrice(product.price)}
                          </span>
                          {product.category && (
                            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                              {product.category}
                            </span>
                          )}
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-300" />
                    </button>
                  </li>
                ))}
              </ul>
              
              {/* View all results link */}
              <div className="border-t border-gray-100 p-3">
                <button
                  onClick={handleSubmit}
                  className="w-full py-2 px-4 text-sm font-medium text-capsule-teal hover:text-capsule-teal-dark hover:bg-capsule-teal/5 rounded-xl transition-colors flex items-center justify-center gap-2"
                  data-testid="view-all-results-btn"
                >
                  <Sparkles className="w-4 h-4" />
                  {t.viewAll} for "{query}"
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </>
          ) : query.length >= 2 && !loading ? (
            <div className="p-6 text-center">
              <div className="w-12 h-12 bg-capsule-teal/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Search className="w-6 h-6 text-capsule-teal" />
              </div>
              <p className="text-gray-500 text-sm">{t.noResults} for "{query}"</p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default SearchBox;
